import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '../../../../../core/services/api.service';
import { DeckProductContext } from '../../deck-admin.models';
import {
  AgentTool,
  AgentToolListResponse,
  CreateAgentToolPayload,
  PatchAgentToolPayload,
} from './agent-tool.models';

/**
 * HTTP client for a product's live-call agent tools (API/tool definitions).
 *
 * ── BACKEND CONTRACT (prism-backend-nodejs, live-call-management/agent) ─────
 * Routes mirror the deck-admin convention: product-scoped by a `:product` path
 * segment (pid or keyname), served by the CRM Node backend under `/api/v1`.
 * Tools are addressed by numeric id. There is no get-one route by design —
 * listings always carry the full record, including `config` and `agents`.
 *
 *   GET    /live-call-agent/:product/tools?includeInactive=0|1
 *            → { product, count, tools: AgentTool[] }   (full records)
 *   POST   /live-call-agent/:product/tools
 *            body: { tool_name, tool_keyname, method?, base_url, config?,
 *                     description?, auth_id?, is_active?, agent_ids? }
 *            → { tool: AgentTool }   (created in this deployment's env)
 *   PATCH  /live-call-agent/:product/tools/:id
 *            body: { tool_name?, method?, base_url?, config?, description?,
 *                     auth_id?, is_active?, for_env?, agent_ids? }
 *            → { tool: AgentTool }   (`agent_ids` REPLACES the assignment set)
 *   DELETE /live-call-agent/:product/tools/:id
 *            → { deleted: boolean }
 */
@Injectable({
  providedIn: 'root',
})
export class AgentToolService {
  private api = inject(ApiService);

  /** Product scope for every request; empty until the page resolves a product. */
  private productContext: DeckProductContext = { productKey: null, productPid: null };

  /** Suppress ApiService's global toaster/loader — the tab renders its own states. */
  private readonly quiet = { showErrorToaster: false, showLoader: false } as const;

  /** Set which product's tools subsequent calls address. */
  setProductContext(context: DeckProductContext): void {
    this.productContext = context;
  }

  /** The `:product` path segment — keyname preferred, pid as fallback. */
  private productSegment(): string | null {
    const { productKey, productPid } = this.productContext;
    if (productKey) return productKey;
    if (productPid != null) return String(productPid);
    return null;
  }

  /** Route prefix for the selected product, or null when no product is set. */
  private basePath(): string | null {
    const segment = this.productSegment();
    return segment ? `/live-call-agent/${encodeURIComponent(segment)}/tools` : null;
  }

  private noProduct(): Observable<never> {
    return throwError(() => ({
      message: 'No product selected — pick a product from the sidebar first.',
    }));
  }

  /** GET the product's tools (full records — no get-one route exists). */
  listTools(includeInactive = true): Observable<AgentToolListResponse> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    const params: Record<string, string | number> = {};
    if (!includeInactive) params['includeInactive'] = 0;
    return this.api
      .get<AgentToolListResponse>(base, params, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** POST a new tool — created visible only in this deployment's env. */
  createTool(payload: CreateAgentToolPayload): Observable<{ tool: AgentTool }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .post<{ tool: AgentTool }>(base, payload, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** PATCH tool fields; `agent_ids` (if given) REPLACES the assignment set. */
  patchTool(toolId: number, payload: PatchAgentToolPayload): Observable<{ tool: AgentTool }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .patch<{ tool: AgentTool }>(`${base}/${toolId}`, payload, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** DELETE one tool. */
  deleteTool(toolId: number): Observable<{ deleted: boolean }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .delete<{ deleted: boolean }>(`${base}/${toolId}`, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** Normalize ApiService errors to the `{status?, message}` the tab renders. */
  private handleError = (error: any): Observable<never> => {
    const message =
      error?.message ||
      error?.error ||
      error?.err ||
      error?.detail ||
      'An unexpected error occurred';
    return throwError(() => ({ status: error?.statusCode ?? error?.status, message, raw: error }));
  };
}
